import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { NoteManager } from 'components/Notes/NoteManager'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { cn } from 'components/UI/cn'
import { BsChevronDown } from 'react-icons/bs'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { usePinTarget } from 'components/QuickAccess/usePinTarget'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import {
  PiCalendarStar,
  PiShippingContainer,
  PiHandshake,
  PiAddressBook,
  PiNotepad,
  PiEnvelope,
  PiFileText,
  PiUploadSimple,
  PiUserPlus,
  PiTag,
  PiTruck,
  PiHandCoins,
  PiArrowsMerge,
  PiPushPin,
  PiPushPinSlash,
  PiTrash,
} from 'react-icons/pi'
import to from 'await-to-js'

// Import modals from the existing CompanyActionsComponent pattern
import CreateDealModal from 'components/Deals/CreateDealModal/CreateDealModal'
import NotesModal from 'components/Modals/NotesModal'
import NewContactModal from 'components/Modals/NewContactModal'
import SuggestionModal from 'components/Modals/SuggestionModal/SuggestionModal'
import { SingleChainMergeModal } from 'features/unmatched-companies/SingleChainMergeModal'
import ReminderModal from 'components/Reminders/ReminderModal'
import {
  SampleRequestFormModal,
  InitialDoorData,
} from 'components/Samples/SampleRequestFormModal'
import {
  SingleItemTagAssignModal,
  SingleItemDistributorAssignModal,
  SingleItemGPOAssignModal,
} from 'components/Actions/SingleItemAssignmentModals'
import { TableTypeSlug, CompanyTypeSlug } from 'models/companies'
import dialogConfirm from 'components/dialogConfirm'
import { UploadDocumentModal } from 'components/DocumentLibrary/UploadDocumentModal'

// Import section modals
import { ContactsModal } from './Modals/ContactsModal'
import { DealsModal } from './Modals/DealsModal'
import { NotesListModal } from './Modals/NotesListModal'
import { EmailsModal } from './Modals/EmailsModal/EmailsModal'
import { DocumentsModal } from './Modals/DocumentsModal'
import { useNavigate, useSearchParams } from 'react-router-dom'

const quickNoteSchema = z.object({
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
})

type QuickNoteInput = z.infer<typeof quickNoteSchema>

interface CompanyTopCardProps {
  chainProxyId?: number
  doorProxyId?: number
  chainID?: number
  contactCompanyID?: number
  companyName?: string
  websiteAddress?: string
  address?: {
    addressInfo?: {
      fullAddress?: string
    }
  }
  phone?: string
  companyTypeSlug: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  isUserCreated?: boolean
  isDoorPage?: boolean
  /** Pre-loaded door data for sample requests - avoids redundant API calls */
  initialDoorData?: InitialDoorData
}

interface ActionButtonProps {
  label: string
  count: number
  icon: React.ReactNode
  onClick?: () => void
  showLabel?: boolean
}

const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  count,
  icon,
  onClick,
  showLabel = true,
}) => (
  // Styling mirrors the homepage SummaryStatsHeader / StatChip — same
  // brand-tinted square icon badge, text-base values and labels with
  // leading-none. Differs in that this is a button (opens a modal), so
  // it keeps the hover bg.
  <button
    onClick={onClick}
    className="group flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-50 transition-colors"
    title={`${label} (${count})`}
  >
    <span className="size-7 inline-flex items-center justify-center rounded-lg bg-brand-active/10 text-brand-active flex-shrink-0">
      {icon}
    </span>
    <span className="text-base font-bold leading-none tabular-nums text-fg">
      {count}
    </span>
    {showLabel && (
      <span className="text-base font-normal leading-none text-gray-500">
        {label}
      </span>
    )}
  </button>
)

const ActionDivider = () => (
  <div className="w-px h-6 bg-line flex-shrink-0" aria-hidden />
)

export function CompanyTopCard({
  chainProxyId,
  doorProxyId,
  chainID,
  companyName,
  websiteAddress,
  address,
  phone,
  companyTypeSlug,
  tableTypeSlug,
  isUserCreated = false,
  isDoorPage = false,
  initialDoorData,
}: CompanyTopCardProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  // Responsive: hide stat labels when the row gets too tight to fit
  // them next to the right-side "All Actions" dropdown. Hysteresis
  // buffer prevents oscillation at the breakpoint when the label
  // hide/show itself changes the row's content width.
  const topCardRef = useRef<HTMLDivElement>(null)
  const actionsRowRef = useRef<HTMLDivElement>(null)
  const statsRowRef = useRef<HTMLDivElement>(null)
  const actionsButtonRef = useRef<HTMLDivElement>(null)

  // Keep --company-top-card-height in sync with the card's actual height.
  // The EventHistoryAccordion's sticky header anchors to this variable; the
  // previous inline-ref approach only wrote the value on mount, so any
  // post-mount resize (lazy-loaded stats, toggling Account Attributes /
  // Deals accordions that reshape the layout, async data filling in) left
  // the sticky offset stale and the header would land in the wrong spot.
  useEffect(() => {
    const el = topCardRef.current
    if (!el) return
    const update = () =>
      document.documentElement.style.setProperty(
        '--company-top-card-height',
        `${el.offsetHeight}px`
      )
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const [showStatLabels, setShowStatLabels] = useState(true)

  useEffect(() => {
    const row = actionsRowRef.current
    const stats = statsRowRef.current
    const actions = actionsButtonRef.current
    if (!row || !stats || !actions) return

    // Estimated extra width contributed by all 5 stat labels (Contacts,
    // Deals, Notes, Emails, Documents) when shown — used to decide
    // whether re-enabling labels would fit again.
    const ESTIMATED_LABEL_WIDTH = 360
    const MIN_GAP = 16

    const measure = () => {
      const rowWidth = row.getBoundingClientRect().width
      const statsWidth = stats.getBoundingClientRect().width
      const actionsWidth = actions.getBoundingClientRect().width

      setShowStatLabels((prev) => {
        if (prev) {
          // Labels currently shown — hide if they no longer fit.
          return statsWidth + actionsWidth + MIN_GAP <= rowWidth
        }
        // Labels currently hidden — only re-show if there's clearly
        // room for them (hysteresis buffer to avoid flicker).
        return (
          statsWidth + ESTIMATED_LABEL_WIDTH + actionsWidth + MIN_GAP + 16 <=
          rowWidth
        )
      })
    }

    measure()
    // Only observe the OUTER row. The stats row's own size changes when
    // labels toggle, which would loop the observer if we observed it.
    const ro = new ResizeObserver(measure)
    ro.observe(row)
    return () => ro.disconnect()
  }, [])

  // Get the primary ID for operations
  const primaryId = chainProxyId || doorProxyId

  const {
    isPinned: pinnedToHome,
    toggle: togglePin,
    isPending: pinPending,
  } = usePinTarget({ targetType: 'company', targetId: chainProxyId ?? 0 })
  useRecordVisit(
    chainProxyId ? { targetType: 'company', targetId: chainProxyId } : null
  )

  // Fetch company or door proxy data based on context
  const { data: effectiveData } = useCompanyOrDoorData(
    chainProxyId || 0,
    doorProxyId
  )

  // Modal states for action buttons
  // Deep-link target for the EmailsModal. When `?email_thread_id=<n>` is in
  // the URL, the modal auto-opens scrolled to that thread (FIR-7104). Stripped
  // on manual close so refreshes don't re-open the modal after the user closed
  // it; kept on initial open so the URL stays shareable / refreshable.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialEmailThreadId = useMemo(() => {
    const raw = searchParams.get('email_thread_id')
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) && n > 0 ? n : undefined
  }, [searchParams])

  const [showContactsModal, setShowContactsModal] = useState(false)
  const [showDealsModal, setShowDealsModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showEmailsModal, setShowEmailsModal] = useState(!!initialEmailThreadId)
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)

  // Modal states for dropdown actions
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [showCreateNote, setShowCreateNote] = useState(false)
  const [showAddContact, setShowAddContact] = useState(false)
  const [showManageTags, setShowManageTags] = useState(false)
  const [showAddDistributor, setShowAddDistributor] = useState(false)
  const [showAddGPO, setShowAddGPO] = useState(false)
  const [showMergeCompany, setShowMergeCompany] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showSampleRequestModal, setShowSampleRequestModal] = useState(false)
  const [showUploadDocument, setShowUploadDocument] = useState(false)

  // Quick note form
  const { control, handleSubmit, reset } = useForm<QuickNoteInput>({
    defaultValues: {
      note: '',
      attachments: [],
    },
    resolver: zodResolver(quickNoteSchema),
  })

  // Watch the note field to determine if Save button should be enabled
  const noteText = useWatch({ control, name: 'note' })

  // Actions dropdown configuration (same as CompanyActionsComponent)
  const dropdownItems: IDropdownItem[] = [
    {
      label: pinnedToHome ? 'Unpin from homepage' : 'Pin to homepage',
      icon: pinnedToHome ? (
        <PiPushPinSlash size={16} />
      ) : (
        <PiPushPin size={16} />
      ),
      hidden: !featureFlag.enableQuickAccess,
      disabled: pinPending,
      callback: () => {
        togglePin()
        toast.success(
          pinnedToHome ? 'Unpinned from homepage' : 'Pinned to homepage'
        )
      },
    },
    {
      label: 'Create a Deal',
      icon: <PiHandshake size={16} />,
      callback: () => setShowCreateDeal(true),
    },
    {
      label: 'Create a Note',
      icon: <PiNotepad size={16} />,
      callback: () => setShowCreateNote(true),
    },
    {
      label: 'Upload Document',
      icon: <PiUploadSimple size={16} />,
      callback: () => setShowUploadDocument(true),
    },
    {
      label: 'Add a Contact',
      icon: <PiUserPlus size={16} />,
      callback: () => setShowAddContact(true),
    },
    {
      label: 'Manage Tags',
      icon: <PiTag size={16} />,
      callback: () => setShowManageTags(true),
    },
    {
      label: 'Add Distributor',
      icon: <PiTruck size={16} />,
      callback: () => setShowAddDistributor(true),
    },
    {
      label: 'Add GPO',
      icon: <PiShippingContainer size={16} />,
      callback: () => setShowAddGPO(true),
    },
    ...(featureFlag.enableSamples
      ? [
          {
            label: 'Sample Request',
            icon: <PiHandCoins size={16} />,
            callback: () => setShowSampleRequestModal(true),
          },
        ]
      : []),
    ...(featureFlag.enableReminders
      ? [
          {
            label: 'Schedule Reminder',
            icon: <PiCalendarStar size={16} />,
            callback: () => setShowReminderModal(true),
          },
        ]
      : []),
    // Conditionally add actions based on whether it's user-created
    ...(!doorProxyId && isUserCreated
      ? [
          {
            label: 'Merge Company',
            icon: <PiArrowsMerge size={16} />,
            callback: () => setShowMergeCompany(true),
          },
        ]
      : []),
    ...(isUserCreated
      ? [
          {
            label: isDoorPage ? 'Delete Door' : 'Delete Company',
            icon: <PiTrash size={16} />,
            callback: async () => {
              const entityLabel = isDoorPage ? 'Door' : 'Company'
              if (
                await dialogConfirm(
                  isDoorPage
                    ? 'Are you sure you want to delete this door? This action cannot be undone.'
                    : 'Deleting this company will also delete any notes, tags, deals, or contacts you may have added. This action cannot be undone.',
                  `Delete ${entityLabel}`,
                  undefined,
                  {
                    buttonVariant: 'danger',
                    confirmLabel: `Delete ${entityLabel}`,
                  }
                )
              ) {
                if (isDoorPage) {
                  if (!doorProxyId) return
                  const [err] = await to(api.deleteDoorProxy(doorProxyId))
                  if (err) {
                    toast.error('Error deleting door')
                    return
                  }
                  toast.success('Door deleted')
                } else {
                  if (!effectiveData?.id) return
                  const [err] = await to(api.deleteCompany(effectiveData.id!))
                  if (err) {
                    toast.error('Error deleting company')
                    return
                  }
                  toast.success('Company deleted')
                }
                navigate('/')
              }
            },
          },
        ]
      : []),
  ]

  // Handle quick note save
  const [isSavingNote, setIsSavingNote] = useState(false)
  const onSubmitQuickNote = handleSubmit(async (values) => {
    // Require text content - attachments alone are not sufficient for a note
    if (values.note?.trim()) {
      setIsSavingNote(true)
      try {
        await api.createNote(
          chainID || chainProxyId,
          chainProxyId,
          values.note || '',
          values.attachments || [],
          doorProxyId
        )
        toast.success('Note saved successfully')
        reset({
          note: '',
          attachments: [],
        })
        handleRefreshData()
      } catch (error) {
        toast.error('Failed to save note')
      } finally {
        setIsSavingNote(false)
      }
    }
  })

  // Refresh data after actions (same logic as CompanyActionsComponent)
  const handleRefreshData = () => {
    // Specifically refetch the company or door proxy query to refresh counts immediately
    if (chainProxyId) {
      queryClient.refetchQueries({
        queryKey: ['company', chainProxyId],
      })
    }
    if (doorProxyId) {
      queryClient.refetchQueries({
        queryKey: ['door_proxy', doorProxyId],
      })
    }

    // Remove cached queries for deals, contacts, notes, and emails modals to force fresh data
    // This ensures that when modals reopen, they fetch fresh data instead of using stale cache
    if (chainProxyId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey
          if (!Array.isArray(key) || key.length === 0) return false

          const tableKey = key[0]
          if (typeof tableKey !== 'string') return false

          // Remove queries for the modal tables
          return (
            tableKey.includes('deals-modal-company') ||
            tableKey.includes('contacts-modal-company') ||
            tableKey.includes('notes-modal-company') ||
            tableKey.includes('emails-modal-company') ||
            tableKey.includes('documents-modal-company')
          )
        },
      })
    }

    if (doorProxyId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey
          if (!Array.isArray(key) || key.length === 0) return false

          const tableKey = key[0]
          if (typeof tableKey !== 'string') return false

          // Remove queries for the modal tables
          return (
            tableKey.includes('deals-modal-door') ||
            tableKey.includes('contacts-modal-door') ||
            tableKey.includes('notes-modal-door') ||
            tableKey.includes('emails-modal-door') ||
            tableKey.includes('documents-modal-door')
          )
        },
      })
    }

    // Invalidate activity tables and company activity to show document actions in activity feed
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.includes('activity-table') ||
            firstKey === 'companyActivity')
        )
      },
    })

    // Also invalidate all other company-related queries (for general updates)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false

        const shouldInvalidate = key.some((k) => {
          if (typeof k === 'number') {
            return k === primaryId || k === chainProxyId || k === doorProxyId
          }
          if (typeof k === 'string') {
            return (
              k.includes('contactCompany') ||
              k.includes('doorProxy') ||
              k.includes('company') ||
              k.includes('chain') ||
              (chainProxyId && k.includes(`${chainProxyId}`)) ||
              (doorProxyId && k.includes(`${doorProxyId}`)) ||
              (primaryId && k.includes(`${primaryId}`))
            )
          }
          return false
        })

        return shouldInvalidate
      },
    })
  }

  // Create fallback contact company object with real data when available
  const effectiveContactCompany: import('models/companies').ContactCompany = {
    id: chainProxyId || 0,
    name: effectiveData?.name || companyName || '',
    domain: effectiveData?.domain || websiteAddress || '',
    note_count: effectiveData?.note_count || 0,
    deal_count: effectiveData?.deal_count || 0,
    contact_count: effectiveData?.contact_count || 0,
  }

  return (
    <>
      <div
        ref={topCardRef}
        className="bg-white rounded-lg border border-line overflow-x-auto pt-3 pb-4 shadow-md sticky top-0 z-[4]"
      >
        {/* Content with horizontal padding */}
        <div className="space-y-5">
          {/* Top Actions Row */}
          <div
            ref={actionsRowRef}
            className="flex items-center justify-between px-4 h-11"
          >
            {/* Left side - Action buttons */}
            <div ref={statsRowRef} className="flex items-center gap-1">
              <ActionButton
                label="Contacts"
                count={effectiveData?.contact_count ?? 0}
                icon={<PiAddressBook size={14} />}
                onClick={() => setShowContactsModal(true)}
                showLabel={showStatLabels}
              />
              <ActionDivider />
              <ActionButton
                label="Deals"
                count={effectiveData?.deal_count ?? 0}
                icon={<PiHandshake size={14} />}
                onClick={() => setShowDealsModal(true)}
                showLabel={showStatLabels}
              />
              <ActionDivider />
              <ActionButton
                label="Notes"
                count={effectiveData?.note_count ?? 0}
                icon={<PiNotepad size={14} />}
                onClick={() => setShowNotesModal(true)}
                showLabel={showStatLabels}
              />
              <ActionDivider />
              <ActionButton
                label="Emails"
                count={effectiveData?.email_count ?? 0}
                icon={<PiEnvelope size={14} />}
                onClick={() => setShowEmailsModal(true)}
                showLabel={showStatLabels}
              />
              <ActionDivider />
              <ActionButton
                label="Documents"
                count={effectiveData?.document_count ?? 0}
                icon={<PiFileText size={14} />}
                onClick={() => setShowDocumentsModal(true)}
                showLabel={showStatLabels}
              />
            </div>

            {/* Right side - Actions dropdown — custom button so the
                "All Actions" label stays visible at all widths
                (Dropdown's built-in label would hide it on narrow). */}
            <div
              ref={actionsButtonRef}
              className="flex items-center justify-end"
            >
              <Dropdown
                align="end"
                buttonComponent={
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg bg-brand-bright hover:bg-brand-active text-white text-base font-medium transition-colors focus:outline-none focus:ring-0"
                  >
                    <span>Actions</span>
                    <BsChevronDown />
                  </button>
                }
                items={dropdownItems}
              />
            </div>
          </div>

          {/* Quick Note Editor */}
          <div className="px-4">
            <div
              className={cn(
                'bg-white rounded-md shadow-sm border border-line overflow-hidden',
                // Strip the inner editor's own top margin and the Quill
                // container's own border + rounded corners so this
                // wrapper owns the entire visual shell with no
                // double-stroke at any seam. The Editor renders its
                // mt-2 on a grandchild (NoteManager wraps it), so
                // target any descendant mt-2 rather than just direct
                // children.
                '[&_.mt-2]:!mt-0',
                '[&_.ql-container]:!border-0 [&_.ql-container]:!rounded-none',
                '[&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!rounded-none'
              )}
            >
              <NoteManager
                control={control}
                noteName="note"
                attachmentsName="attachments"
                placeholder="Start writing your note here"
                enableMentions={true}
                editorClassName="h-36 bg-white border-0"
                toolbarClassName="bg-white border-0"
                label=""
                disabled={isSavingNote}
                toolbarSuffix={
                  // Always render the buttons so the toolbar height
                  // stays constant; hide them visually (and disable
                  // pointer events) when there's no note text.
                  <span
                    className={cn(
                      'inline-flex items-center gap-2',
                      !noteText?.trim() && 'invisible pointer-events-none'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => reset({ note: '', attachments: [] })}
                      className="text-sm text-fg-secondary hover:text-fg transition-colors px-2 py-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSubmitQuickNote}
                      disabled={!noteText?.trim() || isSavingNote}
                      className={cn(
                        'text-sm font-medium transition-colors px-2 py-1',
                        noteText?.trim()
                          ? 'text-brand hover:text-brand-hover'
                          : 'text-fg-secondary cursor-not-allowed'
                      )}
                    >
                      {isSavingNote ? 'Saving...' : 'Save'}
                    </button>
                  </span>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* All Modals (same as CompanyActionsComponent) */}

      {/* Create Deal Modal */}
      {showCreateDeal && (
        <CreateDealModal
          show={showCreateDeal}
          handleClose={() => setShowCreateDeal(false)}
          targetCompanyId={chainProxyId}
          targetDoorProxyId={doorProxyId}
          forDoorSelection={!!doorProxyId}
          tableTypeSlug={tableTypeSlug}
          companyTypeSlug={companyTypeSlug}
          totalSelectedRows={1}
          optimisticUiTarget="deals-section"
          onDealCreated={handleRefreshData}
        />
      )}

      {/* Create Note Modal */}
      {showCreateNote && (
        <NotesModal
          show={showCreateNote}
          handleClose={() => setShowCreateNote(false)}
          chainId={chainID}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          onSubmitSuccess={handleRefreshData}
        />
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <NewContactModal
          show={showAddContact}
          handleClose={() => setShowAddContact(false)}
          onContactCreated={handleRefreshData}
          forCompany={effectiveContactCompany}
          initialDoorIds={doorProxyId ? [doorProxyId] : undefined}
        />
      )}

      {/* Upload Document Modal */}
      {showUploadDocument && (
        <UploadDocumentModal
          isOpen={showUploadDocument}
          onClose={() => setShowUploadDocument(false)}
          preSelectedCompanyId={chainProxyId}
          preSelectedCompanyName={effectiveData?.name || companyName}
          onSuccess={handleRefreshData}
        />
      )}

      {/* Manage Tags Modal */}
      {showManageTags && primaryId && (
        <SingleItemTagAssignModal
          show={showManageTags}
          handleClose={() => setShowManageTags(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Add Distributor Modal */}
      {showAddDistributor && primaryId && (
        <SingleItemDistributorAssignModal
          show={showAddDistributor}
          handleClose={() => setShowAddDistributor(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Add GPO Modal */}
      {showAddGPO && primaryId && (
        <SingleItemGPOAssignModal
          show={showAddGPO}
          handleClose={() => setShowAddGPO(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Schedule Reminder Modal */}
      {featureFlag.enableReminders && showReminderModal && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={chainProxyId}
          defaultDoorProxyId={doorProxyId}
          onCreated={() => {
            setShowReminderModal(false)
            handleRefreshData()
          }}
        />
      )}

      {/* Sample Request Modal */}
      {featureFlag.enableSamples && showSampleRequestModal && (
        <SampleRequestFormModal
          show={showSampleRequestModal}
          onHide={() => {
            setShowSampleRequestModal(false)
            handleRefreshData()
          }}
          initialCompanyId={chainProxyId}
          initialDoorProxyId={doorProxyId}
          initialDoorData={initialDoorData}
        />
      )}

      {/* Merge Company Modal (for user-created companies) */}
      {isUserCreated && chainProxyId && (
        <SingleChainMergeModal
          show={showMergeCompany}
          onClose={() => setShowMergeCompany(false)}
          contactCompanyId={chainProxyId}
          contactCompanyName={companyName ?? ''}
        />
      )}

      {/* Hidden SuggestionModal that we'll trigger programmatically */}
      {!isUserCreated && (
        <div style={{ display: 'none' }}>
          <SuggestionModal
            contactCompanyId={chainProxyId!}
            tableTypeSlug={tableTypeSlug}
            doorId={doorProxyId}
            defaultValues={{
              suggested_name: companyName,
              suggested_website: websiteAddress ?? '',
              ...(doorProxyId && {
                suggested_address: address?.addressInfo?.fullAddress,
                suggested_phone_number: phone,
              }),
            }}
          />
        </div>
      )}

      {/* Contacts Modal */}
      {showContactsModal && effectiveContactCompany && (
        <ContactsModal
          show={showContactsModal}
          onHide={() => setShowContactsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Deals Modal */}
      {showDealsModal && effectiveContactCompany && (
        <DealsModal
          show={showDealsModal}
          onHide={() => setShowDealsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Notes List Modal */}
      {showNotesModal && (
        <NotesListModal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          chainId={chainID}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Emails Modal */}
      {showEmailsModal && effectiveContactCompany && (
        <EmailsModal
          show={showEmailsModal}
          onHide={() => {
            setShowEmailsModal(false)
            // Strip the deep-link param on close so a later refresh doesn't
            // re-open the modal. No-op when the param wasn't there to begin
            // with (manual open from the action bar).
            if (searchParams.has('email_thread_id')) {
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev)
                  next.delete('email_thread_id')
                  return next
                },
                { replace: true }
              )
            }
          }}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
          initialThreadId={initialEmailThreadId}
        />
      )}

      {/* Documents Modal */}
      {showDocumentsModal && effectiveContactCompany && (
        <DocumentsModal
          show={showDocumentsModal}
          onHide={() => setShowDocumentsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}
    </>
  )
}
